import {RearrangedProductSearchRefinement} from 'coreStore/data/productSearchResult/interface';
import RefinementValueModel from './RefinementValueModel';
import {
	REFINEMENT_DISALLOW_LIST,
	SINGLE_SELECTION_FILTERS,
	FILTER_SEARCH_KEY
} from 'brand/app/constants';
import SearchModel from './SearchModel';

/**
 * Represents RefinementModel
 */
class RefinementModel {
	private searchModel: SearchModel;
	private refinement: RearrangedProductSearchRefinement;
	private values: Array<RefinementValueModel>;
	private children: Array<RefinementModel>;

	/**
	 * @constructor
	 * @param {SearchModel} searchModel - search model
	 * @param {RearrangedProductSearchRefinement} refinement - refinement data
	 */
	constructor(
		searchModel: SearchModel,
		refinement: RearrangedProductSearchRefinement
	) {
		this.searchModel = searchModel;
		this.refinement = refinement;
	}

	/**
	 * Returns unique key
	 * @returns {string}
	 */
	getKey(): string {
		return `ref-filter-core-${this.getAttributeId()}${this.getLabel().replace(' ', '_')}`;
	}

	/**
	 * Returns the attribute ID
	 * @returns {string}
	 */
	getAttributeId(): string {
		return this.refinement.attributeId;
	}

	/**
	 * Returns the attribute label
	 * @returns {string}
	 */
	getLabel(): string {
		return this.refinement.label;
	}

	/**
	 * Returns the attribute values
	 * @returns {RefinementValueModel[]}
	 */
	getValues(): Array<RefinementValueModel>  {
		if (!this.values) {
			this.values = this.refinement?.values?.map((v) => new RefinementValueModel(this, v)) || [];
		}

		return this.values;
	}

	/**
	 * Returns an attribute value searching by its key
	 * @returns {RefinementValueModel|null}
	 */
	getRefinementValueModelByKey(key: number): RefinementValueModel|null {
		return this.getValues()[key] ? this.getValues()[key] : null;
	}

	/**
	 * Returns an attribute value searching by its value ( searches also in children )
	 * @returns {RefinementValueModel|null}
	 */
	getRefinementValueModelByValue(value: string, includeNotExistant: boolean = false): RefinementValueModel|null {
		const find = this.getValues().find((rv) => rv.getValue() === value);

		if (find) {
			return find;
		} else {
			for (const r of this.getChildren()) {
				const find = r.getRefinementValueModelByValue(value);

				if (find) {
					return find;
				}
			}
		}

		if (!find && includeNotExistant) { //TODO: verify approach
			return new RefinementValueModel(this, {
				value: value,
				hitCount: 0,
				label: value
			});
		}

		return null;
	}

	/**
	 * Returns the active count number of the values of the current refinement ( searches also in children )
	 * @returns {number}
	 */
	getActiveValuesCount(): number {
		let count = 0;

		count += this.getValues().reduce(
			(c, rv): number =>
				rv.isActive() ? c + 1 : c
			, 0);

		count += this.getChildren().reduce(
			(c, r): number =>
				r.getActiveValuesCount() + c
			, 0);

		return count;
	}

	/**
	 * Returns the children of the current refinement
	 * @param {boolean} includeHidden - if true also hidden ( disallowed and invalid ) refinements will be included
	 * @returns {RefinementModel[]}
	 */
	getChildren(includeHidden: boolean = false): Array<RefinementModel> {
		if (!this.children) {
			this.children = this.refinement?.children?.map((r) => new RefinementModel(this.searchModel, r)) || [];
		}

		if (!includeHidden) {
			return this.children?.filter((r) =>
				!REFINEMENT_DISALLOW_LIST.includes(r?.getAttributeId())
				&& r.isValid()
			);
		} else {
			return this.children;
		}
	}

	/**
	 * Returns true if the refinement is valid to be computed
	 * @returns {boolean}
	 */
	isValid(): boolean {
		return this.getValues().length > 0 || this.getChildren().length > 0;
	}

	/**
	 * Returns true if the refinement must work in single selection mode
	 * @returns {boolean}
	 */
	isSingleSelection(): boolean {
		return SINGLE_SELECTION_FILTERS.indexOf(this.getAttributeId()) !== -1;
	}

	/**
	 * Returns hit count of the current refinement
	 * @returns {number}
	 */
	getHitCount(): number {
		return this.refinement?.hitCount ? this.refinement.hitCount : 0;
	}

	/**
	 * Returns the value of the "select all" refinement
	 * @returns {RefinementValueModel|null}
	 */
	getAllRefinementValue(): RefinementValueModel|null {// TODO: find a better name
		const filterAllIndex = this.getFilterAllIndex();

		if (filterAllIndex !== -1) {
			return this.getRefinementValueModelByKey(filterAllIndex);
		} else {
			return null;
		}
	}

	/**
	 * Returns the search model
	 * @returns {string}
	 */
	getSearchModel() {
		return this.searchModel;
	}

	/**
	 * Returns the index of the value of the "select all" refinement
	 * @returns {number}
	 */
	getFilterAllIndex(): number {
		return this.getValues().findIndex((el) => el.isFilterAll());
	}

	/**
	 * Returns the value of the "select all" refinement
	 * @returns {RefinementValueModel|null}
	 */
	getFilterAll(): RefinementValueModel|null {
		return this.getValues().find((el) => el.isFilterAll());
	}

	/**
	 * Returns true if the refinement should work in searchMode
	 * @returns {boolean}
	 */
	isSearchMode(): boolean {
		return FILTER_SEARCH_KEY.indexOf(this.getAttributeId()) !== -1;
	}
}

export default RefinementModel;
